import React from 'react'
import { FileSearchOutlined, SearchOutlined } from '@ant-design/icons'
import {
  Badge,
  Button,
  Card,
  Flex,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import { Link } from 'react-router-dom'
import { GUARD_RULES } from 'src/features/session/constants'
import { Guard } from 'src/features/session/guard'
import { path } from '../../../libs/path'
import { WalletGuardWallet } from '../types'
import {
  getWalletBalance,
  getWalletCheckStatus,
  getWalletName,
  getWalletStatus,
  isWalletChecking,
  shortenWalletAddress,
} from './wallets'
import styles from './wallets.module.css'

type Props = {
  wallet: WalletGuardWallet
  onCheck: (wallet: WalletGuardWallet) => void
}

export const WGDashboardWalletCard = ({ wallet, onCheck }: Props) => {
  const { token } = theme.useToken()
  const name = getWalletName(wallet.name, wallet.address)
  const status = getWalletStatus(wallet.status)
  const checkStatus = getWalletCheckStatus(wallet.checkStatus)
  const checking = isWalletChecking(wallet.checkStatus)
  const statusColor = {
    success: token.colorSuccess,
    warning: token.colorWarning,
    error: token.colorError,
    processing: token.colorPrimary,
    default: token.colorTextSecondary,
  }[status.badge ?? 'default']

  return (
    <Card
      size="small"
      className={styles.walletCard}
      styles={{ body: { padding: token.paddingSM } }}
      style={{
        borderInlineStartWidth: 3,
        borderInlineStartColor:
          wallet.status === 'UNCHECKED'
            ? token.colorBorderSecondary
            : statusColor,
      }}
    >
      <Flex vertical gap={6}>
        <Flex align="center" gap={6}>
          <Typography.Text strong ellipsis title={name} style={{ minWidth: 0 }}>
            {name}
          </Typography.Text>
          <Tag
            bordered={false}
            style={{
              marginInlineEnd: 0,
              color: token.colorPrimaryText,
              background: token.colorPrimaryBg,
              fontSize: token.fontSizeSM,
              lineHeight: '18px',
              flexShrink: 0,
            }}
          >
            {wallet.currency}
          </Tag>
        </Flex>

        <Tooltip title={wallet.address}>
          <Typography.Text
            className={styles.address}
            style={{
              background: token.colorFillTertiary,
              borderRadius: token.borderRadiusSM,
              color: token.colorTextSecondary,
              fontSize: token.fontSizeSM,
            }}
          >
            {shortenWalletAddress(wallet.address)}
          </Typography.Text>
        </Tooltip>

        <Typography.Text className={styles.balance}>
          {getWalletBalance(wallet.balance, wallet.currency)}
        </Typography.Text>

        <Badge
          status={status.badge}
          text={<span style={{ color: statusColor }}>{status.label}</span>}
        />
        {wallet.checkStatus !== 'COMPLETED' &&
          checkStatus.label !== status.label && (
            <Badge status={checkStatus.badge} text={checkStatus.label} />
          )}

        <Flex gap={4}>
          <Tooltip title="Просмотр кошелька">
            <Link to={path.walletGuard.walletDetails(String(wallet.id))}>
              <Button
                size="small"
                type="text"
                icon={<SearchOutlined />}
                aria-label={`Просмотр кошелька ${name}`}
                style={{ background: token.colorFillTertiary }}
              />
            </Link>
          </Tooltip>
          <Guard permission={GUARD_RULES.CHECK_CRYPTO_WALLET} mode="permission">
            <Tooltip
              title={checking ? 'Проверка уже выполняется' : 'Ручная проверка'}
            >
              <Button
                size="small"
                type="text"
                icon={<FileSearchOutlined />}
                aria-label={`Ручная проверка кошелька ${name}`}
                disabled={checking}
                loading={checking}
                onClick={() => onCheck(wallet)}
                style={{ background: token.colorFillTertiary }}
              />
            </Tooltip>
          </Guard>
        </Flex>
      </Flex>
    </Card>
  )
}
